import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Search, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  User, 
  Calendar,
  MapPin,
  Phone,
  Mail,
  Copy,
  ArrowRightCircle,
  ShieldCheck,
  Eye,
  XCircle,
  Send,
} from 'lucide-react';

interface GrievanceStatusItem {
  id: string;
  status: string;
  description: string;
  date: string;
  officer?: string;
  comments?: string;
}

interface GrievanceDetails {
  id: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  submittedDate: string;
  lastUpdated: string;
  estimatedResolution: string;
  fullName: string;
  email: string;
  phone: string;
  address: string;
  district: string;
  assignedOfficer?: string;
  department?: string;
  timeline: GrievanceStatusItem[];
}

/* ─── Status label map (Nepali topic headings) ─── */
const STATUS_LABEL_NP: Record<string, string> = {
  SUBMITTED: 'दर्ता भयो',
  APPROVED: 'स्वीकृत भयो',
  MONITORING: 'अनुगमनमा छ',
  'IN-PROGRESS': 'कार्यान्वयनमा छ',
  IN_PROGRESS: 'कार्यान्वयनमा छ',
  RESOLVED: 'समाधान भयो',
  REJECTED: 'अस्वीकृत भयो',
  FORWARDED: 'फर्वार्ड गरियो',
  UNDER_REVIEW: 'समीक्षामा छ',
  CLOSED: 'बन्द भयो',
};

const STATUS_LABEL_EN: Record<string, string> = {
  SUBMITTED: 'Registered',
  APPROVED: 'Approved',
  MONITORING: 'Under Monitoring',
  'IN-PROGRESS': 'In Progress',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  REJECTED: 'Rejected',
  FORWARDED: 'Forwarded',
  UNDER_REVIEW: 'Under Review',
  CLOSED: 'Closed',
};

/* ─── Color & icon config per status ─── */
const STATUS_CONFIG: Record<string, {
  gradient: string;
  bg: string;
  text: string;
  border: string;
  iconBg: string;
  icon: React.ReactNode;
  badgeBg: string;
  badgeText: string;
  ring: string;
}> = {
  SUBMITTED: {
    gradient: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
    icon: <Send className="h-4 w-4 text-white" />,
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-800',
    ring: 'ring-blue-400',
  },
  APPROVED: {
    gradient: 'from-emerald-500 to-emerald-600',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    icon: <ShieldCheck className="h-4 w-4 text-white" />,
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-800',
    ring: 'ring-emerald-400',
  },
  MONITORING: {
    gradient: 'from-amber-500 to-amber-600',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    iconBg: 'bg-gradient-to-br from-amber-500 to-amber-600',
    icon: <Eye className="h-4 w-4 text-white" />,
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-800',
    ring: 'ring-amber-400',
  },
  'IN-PROGRESS': {
    gradient: 'from-orange-500 to-orange-600',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    iconBg: 'bg-gradient-to-br from-orange-500 to-orange-600',
    icon: <Clock className="h-4 w-4 text-white" />,
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-800',
    ring: 'ring-orange-400',
  },
  IN_PROGRESS: {
    gradient: 'from-orange-500 to-orange-600',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    iconBg: 'bg-gradient-to-br from-orange-500 to-orange-600',
    icon: <Clock className="h-4 w-4 text-white" />,
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-800',
    ring: 'ring-orange-400',
  },
  RESOLVED: {
    gradient: 'from-green-500 to-green-600',
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
    icon: <CheckCircle className="h-4 w-4 text-white" />,
    badgeBg: 'bg-green-100',
    badgeText: 'text-green-800',
    ring: 'ring-green-400',
  },
  REJECTED: {
    gradient: 'from-red-500 to-red-600',
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    iconBg: 'bg-gradient-to-br from-red-500 to-red-600',
    icon: <XCircle className="h-4 w-4 text-white" />,
    badgeBg: 'bg-red-100',
    badgeText: 'text-red-800',
    ring: 'ring-red-400',
  },
  CLOSED: {
    gradient: 'from-gray-500 to-gray-600',
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
    iconBg: 'bg-gradient-to-br from-gray-500 to-gray-600',
    icon: <CheckCircle className="h-4 w-4 text-white" />,
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-800',
    ring: 'ring-gray-400',
  },
  UNDER_REVIEW: {
    gradient: 'from-yellow-500 to-yellow-600',
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    iconBg: 'bg-gradient-to-br from-yellow-500 to-yellow-600',
    icon: <Eye className="h-4 w-4 text-white" />,
    badgeBg: 'bg-yellow-100',
    badgeText: 'text-yellow-800',
    ring: 'ring-yellow-400',
  },
};

const DEFAULT_STATUS_CONFIG = {
  gradient: 'from-gray-500 to-gray-600',
  bg: 'bg-gray-50',
  text: 'text-gray-700',
  border: 'border-gray-200',
  iconBg: 'bg-gradient-to-br from-gray-500 to-gray-600',
  icon: <FileText className="h-4 w-4 text-white" />,
  badgeBg: 'bg-gray-100',
  badgeText: 'text-gray-800',
  ring: 'ring-gray-400',
};

const TrackGrievancePage: React.FC = () => {
  const { language } = useLanguage();
  const { grievanceId } = useParams<{ grievanceId: string }>();
  const navigate = useNavigate();
  
  const [searchId, setSearchId] = useState(grievanceId || '');
  const [isSearching, setIsSearching] = useState(false);
  const [grievanceDetails, setGrievanceDetails] = useState<GrievanceDetails | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  // Fetch grievance by ID from backend
  const fetchGrievance = async (gid: string) => {
    if (!gid) return;
    setIsSearching(true);
    setError('');
    try {
      const res = await api.get(`/grievances/${gid}`);
      const data = res.data?.data || res.data;
      const timeline = res.data?.timeline || [];
      if (!data) {
        setError(language === 'en' ? 'Grievance not found' : 'गुनासो फेला परेन');
        setGrievanceDetails(null);
        return;
      }
      const mapped: GrievanceDetails = {
        id: data.grievanceId,
        subject: data.subject,
        description: data.description,
        category: data.category,
        priority: (data.priority || '').toUpperCase(),
        status: (data.status || '').toUpperCase(),
        submittedDate: data.submittedAt,
        lastUpdated: data.updatedAt || data.submittedAt,
        estimatedResolution: '',
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        address: data.address,
        district: data.district,
        timeline: timeline.map((t: any) => ({
          id: String(t.id),
          status: String(t.status || '').toUpperCase(),
          description: t.message,
          date: t.eventDate,
          officer: t.office,
        })),
      };
      setGrievanceDetails(mapped);
    } catch (e: any) {
      setError(e?.response?.data?.message || (language === 'en' ? 'Failed to fetch grievance' : 'गुनासो ल्याउन असफल'));
      setGrievanceDetails(null);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (grievanceId) {
      setSearchId(grievanceId);
      fetchGrievance(grievanceId);
    }
  }, [grievanceId]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  
  const handleSearch = async () => {
    if (!searchId.trim()) {
      setError(language === 'en' ? 'Please enter a grievance ID' : 'कृपया गुनासो आईडी प्रविष्ट गर्नुहोस्');
      return;
    }
    await fetchGrievance(searchId.trim());
    if (!error) {
      navigate(`/track/${searchId.trim()}`);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(grievanceDetails?.id || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return 'bg-blue-100 text-blue-800';
      case 'UNDER_REVIEW': return 'bg-yellow-100 text-yellow-800';
      case 'IN_PROGRESS': case 'IN-PROGRESS': return 'bg-orange-100 text-orange-800';
      case 'RESOLVED': return 'bg-green-100 text-green-800';
      case 'CLOSED': return 'bg-gray-100 text-gray-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'APPROVED': return 'bg-emerald-100 text-emerald-800';
      case 'MONITORING': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return <FileText className="h-5 w-5" />;
      case 'UNDER_REVIEW': return <Clock className="h-5 w-5" />;
      case 'IN_PROGRESS': case 'IN-PROGRESS': return <AlertCircle className="h-5 w-5" />;
      case 'RESOLVED': return <CheckCircle className="h-5 w-5" />;
      case 'CLOSED': return <CheckCircle className="h-5 w-5" />;
      case 'REJECTED': return <AlertCircle className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const normalized = status.toUpperCase();
    return language === 'en'
      ? STATUS_LABEL_EN[normalized] || status
      : STATUS_LABEL_NP[normalized] || status;
  };

  const getConfig = (status: string) => {
    return STATUS_CONFIG[status.toUpperCase()] || DEFAULT_STATUS_CONFIG;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(language === 'en' ? 'en-US' : 'ne-NP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /* Check if message implies a forward action */
  const isForwardMessage = (msg: string) => {
    if (!msg) return false;
    const lower = msg.toLowerCase();
    return lower.includes('forward') || lower.includes('फर्वार्ड') || lower.includes('पठाइएको');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Inline styles for pulse animation */}
      <style>{`
        @keyframes timeline-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
        }
        .timeline-pulse-dot {
          animation: timeline-pulse 2s ease-in-out infinite;
        }
        @keyframes timeline-fade-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .timeline-entry {
          animation: timeline-fade-in 0.4s ease-out forwards;
        }
      `}</style>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Search Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {language === 'en' ? 'Track Your Grievance' : 'आफ्नो गुनासो ट्र्याक गर्नुहोस्'}
            </h1>
            <p className="text-gray-600">
              {language === 'en' 
                ? 'Enter your grievance ID to track the status and updates'
                : 'स्थिति र अपडेटहरू ट्र्याक गर्न आफ्नो गुनासो आईडी प्रविष्ट गर्नुहोस्'
              }
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1"
                placeholder={language === 'en' ? 'Enter Grievance ID (e.g., GRV-20241201-001)' : 'गुनासो आईडी प्रविष्ट गर्नुहोस् (उदाहरण: GRV-20241201-001)'}
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="btn-primary"
              >
                {isSearching ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </button>
            </div>
            
            {error && (
              <p className="form-error mt-2 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {error}
              </p>
            )}
          </div>

          {/* Helper text */}
          <div className="mt-6 text-center text-sm text-gray-500">
            {language === 'en' ? 'Use the grievance ID you received after submission.' : 'पेश गरेपछि प्राप्त भएको गुनासो आईडी प्रयोग गर्नुहोस्।'}
          </div>
        </div>

        {/* Grievance Details */}
        {grievanceDetails && (
          <div className="space-y-8">
            {/* Header with ID */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {grievanceDetails.subject}
                  </h2>
                  <div className="flex items-center gap-4 mt-2">
                    <span className={`badge ${getStatusColor(grievanceDetails.status)}`}>
                      {getStatusIcon(grievanceDetails.status)}
                      <span className="ml-1">
                        {language === 'en' ? grievanceDetails.status.replace('_', ' ') : grievanceDetails.status}
                      </span>
                    </span>
                    <span className="text-sm text-gray-500">
                      ID: {grievanceDetails.id}
                    </span>
                    <button
                      onClick={copyToClipboard}
                      className="text-primary-600 hover:text-primary-800"
                      title={language === 'en' ? 'Copy ID' : 'आईडी कपी गर्नुहोस्'}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {copied && (
                  <span className="text-sm text-green-600">
                    {language === 'en' ? 'Copied!' : 'कपी गरियो!'}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'en' ? 'Submitted' : 'पेश गरिएको'}
                    </p>
                    <p className="font-medium">{formatDate(grievanceDetails.submittedDate)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'en' ? 'Last Updated' : 'अन्तिम अपडेट'}
                    </p>
                    <p className="font-medium">{formatDate(grievanceDetails.lastUpdated)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">
                      {language === 'en' ? 'Estimated Resolution' : 'अनुमानित समाधान'}
                    </p>
                    <p className="font-medium">{grievanceDetails.estimatedResolution || '—'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Grievance Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Details */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {language === 'en' ? 'Grievance Details' : 'गुनासो विवरण'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="label">{language === 'en' ? 'Category' : 'श्रेणी'}</label>
                    <p className="text-gray-900">{grievanceDetails.category}</p>
                  </div>
                  
                  <div>
                    <label className="label">{language === 'en' ? 'Priority' : 'प्राथमिकता'}</label>
                    <span className={`badge ${
                      grievanceDetails.priority === 'URGENT' ? 'bg-red-100 text-red-800' :
                      grievanceDetails.priority === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                      grievanceDetails.priority === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {grievanceDetails.priority}
                    </span>
                  </div>
                  
                  <div>
                    <label className="label">{language === 'en' ? 'Description' : 'विवरण'}</label>
                    <p className="text-gray-900 whitespace-pre-wrap">{grievanceDetails.description}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {language === 'en' ? 'Contact Information' : 'सम्पर्क जानकारी'}
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">{language === 'en' ? 'Name' : 'नाम'}</p><br />
                      {/* <p className="font-medium">{grievanceDetails.fullName}</p> */}
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">{language === 'en' ? 'Email' : 'इमेल'}</p><br />
                      {/* <p className="font-medium">{grievanceDetails.email}</p> */}
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">{language === 'en' ? 'Phone' : 'फोन'}</p><br />
                      {/* <p className="font-medium">{grievanceDetails.phone}</p> */}
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">{language === 'en' ? 'Address' : 'ठेगाना'}</p><br />
                      {/* <p className="font-medium">{grievanceDetails.address}, {grievanceDetails.district}</p> */}
                    </div>
                  </div>
                </div>

                {grievanceDetails.assignedOfficer && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {language === 'en' ? 'Assigned Officer' : 'नियुक्त अधिकारी'}
                    </h4>
                    <p className="text-gray-900">{grievanceDetails.assignedOfficer}</p>
                    {grievanceDetails.department && (
                      <p className="text-sm text-gray-500">{grievanceDetails.department}</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ═══════════ Attractive Status Timeline ═══════════ */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Timeline Header with gradient */}
              <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 px-6 py-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {language === 'en' ? 'Status Timeline' : 'स्थिति समयरेखा'}
                </h3>
                <p className="text-blue-100 text-sm mt-1">
                  {language === 'en'
                    ? 'Track every update on your grievance'
                    : 'तपाईंको गुनासोको प्रत्येक अपडेट ट्र्याक गर्नुहोस्'}
                </p>
              </div>
              
              <div className="p-6">
                {grievanceDetails.timeline.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-40" />
                    <p className="text-lg font-medium">
                      {language === 'en' ? 'No timeline entries yet' : 'अहिलेसम्म कुनै अपडेट छैन'}
                    </p>
                    <p className="text-sm mt-1">
                      {language === 'en' ? 'Updates will appear here once processed' : 'प्रशोधन पछि अपडेटहरू यहाँ देखिनेछ'}
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Vertical connector line */}
                    <div
                      className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-300 via-gray-200 to-gray-100"
                      style={{ zIndex: 0 }}
                    />

                    <div className="space-y-0">
                      {grievanceDetails.timeline.map((item, index) => {
                        const cfg = getConfig(item.status);
                        const isLatest = index === 0;
                        const isForward = isForwardMessage(item.description);

                        return (
                          <div
                            key={item.id}
                            className="timeline-entry relative flex items-start gap-4"
                            style={{ animationDelay: `${index * 0.08}s`, zIndex: 1 }}
                          >
                            {/* ── Icon node ── */}
                            <div className="relative flex-shrink-0 flex flex-col items-center" style={{ width: 40 }}>
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${cfg.iconBg} ${
                                  isLatest ? 'timeline-pulse-dot ring-2 ring-offset-2 ' + cfg.ring : ''
                                }`}
                              >
                                {isForward ? <ArrowRightCircle className="h-4 w-4 text-white" /> : cfg.icon}
                              </div>
                            </div>

                            {/* ── Content card ── */}
                            <div
                              className={`flex-1 mb-6 rounded-lg border ${cfg.border} ${cfg.bg} p-4 transition-all duration-200 hover:shadow-md ${
                                isLatest ? 'ring-1 ' + cfg.ring + ' shadow-sm' : ''
                              }`}
                            >
                              {/* Status heading */}
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${cfg.badgeBg} ${cfg.badgeText}`}
                                  >
                                    {isForward ? (
                                      <ArrowRightCircle className="h-3.5 w-3.5" />
                                    ) : null}
                                    {isForward
                                      ? (language === 'en' ? 'Forwarded' : 'फर्वार्ड गरियो')
                                      : getStatusLabel(item.status)}
                                  </span>
                                  {isLatest && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                                      {language === 'en' ? 'Latest' : 'नवीनतम'}
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatDate(item.date)}
                                </span>
                              </div>

                              {/* Description / message */}
                              {item.description && (
                                <p className={`text-sm leading-relaxed ${cfg.text} mt-1`}>
                                  {item.description}
                                </p>
                              )}

                              {/* Office / forwarded-to info */}
                              {item.officer && (
                                <div className="mt-3 flex items-center gap-2 text-xs">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/70 border border-gray-200 text-gray-600">
                                    <MapPin className="h-3 w-3" />
                                    {language === 'en' ? 'Office' : 'कार्यालय'}: <strong>{item.officer}</strong>
                                  </span>
                                </div>
                              )}

                              {/* Comments */}
                              {item.comments && (
                                <div className="mt-3 p-3 bg-white/60 rounded-lg border border-gray-100">
                                  <p className="text-xs text-gray-600 italic">"{item.comments}"</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => navigate('/submit')}
                  className="btn-primary"
                >
                  {language === 'en' ? 'Submit New Grievance' : 'नयाँ गुनासो पेश गर्नुहोस्'}
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="btn-outline"
                >
                  {language === 'en' ? 'Back to Home' : 'गृहमा फिर्ता'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackGrievancePage;
